import type { LogRecord } from "@logtape/logtape";

/**
 * Shapes `api_timing` into CloudWatch Embedded Metric Format so CloudWatch
 * extracts it as a metric automatically. Only the server's event sink uses
 * this — the browser sends the same event to RUM via `getRumSink`.
 */

const NAMESPACE = `BillWise/${process.env.NEXT_PUBLIC_ENV ?? "local"}`;

/**
 * `cached` sits alongside `route` because a percentile that blends Redis hits
 * with real upstream calls is meaningless. Filter `cached="false"` for true
 * upstream latency; compare SampleCount across both values for hit rate.
 */
const DIMENSIONS = [["api", "route", "cached"]];

const apiTimingEmf = (p: Record<string, unknown>, timestamp: number) => ({
  _aws: {
    Timestamp: timestamp,
    CloudWatchMetrics: [
      {
        Namespace: NAMESPACE,
        Dimensions: DIMENSIONS,
        Metrics: [{ Name: "ApiLatency", Unit: "Milliseconds" }],
      },
    ],
  },
  // CloudWatch drops the metric if a dimension value isn't a string.
  api: String(p.api),
  route: String(p.route),
  cached: String(p.cached),
  ApiLatency: p.latencyMs,
  // Fields, not dimensions: searchable in Logs Insights, not billed as metrics.
  event: p.event,
  method: p.method,
  status: p.status,
  ok: p.ok,
  runtime: p.runtime,
  ...(p.page === undefined ? {} : { page: p.page }),
  ...(p.reqId === undefined ? {} : { reqId: p.reqId }),
  ...(p.pageLoadId === undefined ? {} : { pageLoadId: p.pageLoadId }),
  ...(p.cacheMs === undefined ? {} : { cacheMs: p.cacheMs }),
  ...(p.reason === undefined ? {} : { reason: p.reason }),
});

export const emfFormatter = (record: LogRecord): string => {
  const p = record.properties as Record<string, unknown>;
  // status 0 is a timeout or dropped connection: its duration is the timeout
  // ceiling, not a measurement, so keep it out of the latency percentiles.
  // Still logged, so Logs Insights can count them.
  if (p.event !== "api_timing" || p.status === 0) return JSON.stringify(p);
  return JSON.stringify(apiTimingEmf(p, record.timestamp));
};
